/**
 * documentExtract.ts — client-side text extraction from .txt and .pdf files.
 *
 * Extracted text is capped at MAX_DOC_CHARS and flagged as truncated if exceeded.
 * Caller is responsible for passing the extracted text to the session.
 */

import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Configure pdfjs worker (Vite ?url suffix resolves to hashed asset path)
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export const MAX_DOC_CHARS = 50_000;

export const ALLOWED_DOC_TYPES = [
  'text/plain',
  'application/pdf',
];

export const ALLOWED_DOC_EXTENSIONS = ['.txt', '.pdf'];

export interface ExtractedDocument {
  text: string;
  filename: string;
  charCount: number;
  truncated: boolean;
}

export async function extractDocumentText(file: File): Promise<ExtractedDocument> {
  let raw: string;

  if (file.type === 'application/pdf') {
    raw = await extractPdfText(file);
  } else {
    raw = await file.text();
  }

  const truncated = raw.length > MAX_DOC_CHARS;
  const text = truncated ? raw.slice(0, MAX_DOC_CHARS) : raw;

  return {
    text,
    filename: file.name,
    charCount: text.length,
    truncated,
  };
}

async function extractPdfText(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const pageTexts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ');
    pageTexts.push(pageText);
  }

  return pageTexts.join('\n\n');
}
